import type { ProgressStatus } from "../types/resume";

/**
 * Typed registry of the resume-builder routes.
 *
 * Order, titles, and the `showInProgress` (exclude) flags are ported verbatim from
 * the ordered array in
 * `src/app/components/ResumeBuilder/ResumeBuilderLayout/ResumeBuilderLayout.jsx`
 * (the array that drives `currentRouteName` and `nextRoute`). Runtime behavior is
 * canonical — do not reorder.
 *
 * The layout array is the sequence the "next" traversal walks, so `getNextRoute`
 * stays a faithful port only if this list matches it entry-for-entry, including the
 * excluded trailing entries and the terminal `review` fallback.
 *
 * Note: `create-resume-options` and `select-option` are entry-funnel routes that the
 * layout array does not contain; they are intentionally absent here so the index math
 * in `getNextRoute` matches the layout exactly.
 */

export interface ResumeRouteEntry {
	/** Stable id (equals the relative path). */
	id: string;
	/** Relative path segment, e.g. "contact-information". */
	path: string;
	/** Human-readable section title. */
	title: string;
	/** `false` for entries the layout flags `exclude` (hidden from the progress rail). */
	showInProgress: boolean;
	/** The `progressStatus` key this route maps to, for the five tracked sections. */
	progressKey?: keyof ProgressStatus;
}

export const RESUME_ROUTES: ResumeRouteEntry[] = [
	{ id: "select-experience", path: "select-experience", title: "Select Experience", showInProgress: true },
	{ id: "select-template", path: "select-template", title: "Select Template", showInProgress: true },
	{
		id: "contact-information",
		path: "contact-information",
		title: "Personal Information",
		showInProgress: true,
		progressKey: "HEADING",
	},
	{
		id: "work-history-summary",
		path: "work-history-summary",
		title: "Work History",
		showInProgress: true,
		progressKey: "EXPERIENCE",
	},
	{
		id: "education-summary",
		path: "education-summary",
		title: "Education",
		showInProgress: true,
		progressKey: "EDUCATION",
	},
	{ id: "skills", path: "skills", title: "Skills", showInProgress: true, progressKey: "SKILLS" },
	{
		id: "brief-summary",
		path: "brief-summary",
		title: "Professional Summary",
		showInProgress: true,
		progressKey: "SUMMARY",
	},
	{ id: "extras", path: "extras", title: "Extras", showInProgress: true },
	{ id: "personal-links", path: "personal-links", title: "Personal Links", showInProgress: true },
	{ id: "accomplishments", path: "accomplishments", title: "Accomplishments", showInProgress: true },
	{ id: "affiliations", path: "affiliations", title: "Affiliations", showInProgress: true },
	{ id: "certifications", path: "certifications", title: "Certifications", showInProgress: true },
	{ id: "languages", path: "languages", title: "Languages", showInProgress: true },
	{
		id: "additional-information",
		path: "additional-information",
		title: "Additional Information",
		showInProgress: true,
	},
	{ id: "reference-summary", path: "reference-summary", title: "References", showInProgress: true },
	{ id: "personal-profile", path: "personal-profile", title: "Personal Profile", showInProgress: true },
	{ id: "custom-extra", path: "custom-extra", title: "Custom Extra", showInProgress: true },
	// Below here the layout sets `exclude: true` — hidden from the progress rail.
	{ id: "work-history", path: "work-history", title: "Work History", showInProgress: false },
	{ id: "work-details", path: "work-details", title: "Work Details", showInProgress: false },
	{ id: "education-information", path: "education-information", title: "Education Information", showInProgress: false },
	{ id: "reference-information", path: "reference-information", title: "Reference Information", showInProgress: false },
	// Terminal fallback target for the "next" traversal.
	{ id: "review", path: "review", title: "Review", showInProgress: true },
];

/** The terminal route the layout falls back to when there is no valid next entry. */
export const REVIEW_ROUTE: ResumeRouteEntry =
	RESUME_ROUTES.find((route) => route.path === "review") ??
	({ id: "review", path: "review", title: "Review", showInProgress: true } as ResumeRouteEntry);

/** Return the registry entry for a relative section path, or `undefined`. */
export function getRouteByPath(section: string): ResumeRouteEntry | undefined {
	return RESUME_ROUTES.find((route) => route.path === section);
}

/**
 * Replicate the layout's "next" logic: find the current entry, step one forward, and
 * return that entry when it exists and is not excluded; otherwise fall back to review.
 *
 * (In the layout, `!filteredData[next].exclude` is equivalent to
 * `filteredData[next].showInProgress`, since every excluded entry has
 * `showInProgress: false`.)
 */
export function getNextRoute(section: string): ResumeRouteEntry {
	const currentIndex = RESUME_ROUTES.findIndex((route) => route.path === section);
	const next = RESUME_ROUTES[currentIndex + 1];
	if (next && next.showInProgress) {
		return next;
	}
	return REVIEW_ROUTE;
}
